import React, { useEffect, useState, useMemo, useCallback } from "react";
import useLatestRequest from "../utils/useLatestRequest";
import { Card, Button, Space, Spin, message, Select, DatePicker, Switch, Table } from "antd";
import { FaArrowLeft, FaDownload } from "react-icons/fa";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
  getCurrentFinancialYear,
  getFinancialYearOptions,
} from "../utils/financialYearUtils";
import {
  REPORT_DATE_FORMAT,
  parseStoredDateRange,
  serializeDateRange,
  formatReportDate,
} from "../utils/reportDateRangeUtils";
import {
  DEFAULT_SUFFIX_OPTIONS,
  DEFAULT_SELECTED_SUFFIXES,
  selectAllDropdownRender,
} from "../utils/suffixFilterUtils";

const resolveSalesOrderId = (row = {}) => {
  if (row.soId) return row.soId;
  if (row.salesOrderId) {
    return typeof row.salesOrderId === "object"
      ? row.salesOrderId._id || row.salesOrderId.id
      : row.salesOrderId;
  }
  if (row._id) return row._id;
  if (row.id) return row.id;
  return null;
};

const FILTERS_STORAGE_KEY = "report:property-media-wise-sales:filters";

const thStyle = {
  backgroundColor: "#daeffe",
  fontSize: "12px",
  fontWeight: "bold",
  padding: "8px 4px",
  border: "1px solid #e8e8e8",
  position: "sticky",
  top: 0,
  zIndex: 2,
  boxShadow: "0 1px 0 0 #e8e8e8",
};
const tdStyle = { padding: "6px 8px", border: "1px solid #e8e8e8", fontSize: "12px", color: "#333" };

const { RangePicker } = DatePicker;
const { Option } = Select;

const formatCurrency = (amount) => {
  if (amount === "" || amount == null) return "-";
  const n = Number(amount);
  if (!n) return "-";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(n);
};

/** Root grand totals row — show 0 and decimals from API */
const formatGrandTotalAmount = (v) => {
  if (v == null || v === "") return "";
  const n = Number(v);
  if (Number.isNaN(n)) return "";
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

const PropertyAndMediaWiseSalesReport = ({ onBack }) => {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [propertyGroups, setPropertyGroups] = useState([]);
  const [uniqueCodeNo, setUniqueCodeNo] = useState([]);
  const [uniquePartyName, setUniquePartyName] = useState([]);
  const [mediaPlanners, setMediaPlanners] = useState([]);
  const [selectedPropertyGroups, setSelectedPropertyGroups] = useState([]);
  const [selectedCodeNo, setSelectedCodeNo] = useState([]);
  const [selectedPartyName, setSelectedPartyName] = useState(undefined);
  const [selectedMediaPlanners, setSelectedMediaPlanners] = useState(undefined);
  const [selectedSuffix, setSelectedSuffix] = useState(DEFAULT_SELECTED_SUFFIXES);
  const [dateRange, setDateRange] = useState(null);
  const [financialYear, setFinancialYear] = useState(getCurrentFinancialYear());
  const [isSummary, setIsSummary] = useState(false);
  const [summaryTotal, setSummaryTotal] = useState(null);
  const [grandTotals, setGrandTotals] = useState(null);
  const [filtersHydrated, setFiltersHydrated] = useState(false);

  // Restore filters on mount (Back navigation / refresh).
  useEffect(() => {
    try {
      const raw = window.sessionStorage.getItem(FILTERS_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return;

      if (typeof parsed.financialYear === "string" && parsed.financialYear) {
        setFinancialYear(parsed.financialYear);
      }
      if (typeof parsed.isSummary === "boolean") {
        setIsSummary(parsed.isSummary);
      }
      if (Array.isArray(parsed.selectedPropertyGroups)) {
        setSelectedPropertyGroups(parsed.selectedPropertyGroups.filter(Boolean));
      }
      if (Array.isArray(parsed.selectedCodeNo)) {
        setSelectedCodeNo(parsed.selectedCodeNo.filter(Boolean));
      }
      if (typeof parsed.selectedPartyName === "string") {
        setSelectedPartyName(parsed.selectedPartyName || undefined);
      }
      if (typeof parsed.selectedMediaPlanners === "string") {
        setSelectedMediaPlanners(parsed.selectedMediaPlanners || undefined);
      }
      if (Array.isArray(parsed.selectedSuffix)) {
        const restored = parsed.selectedSuffix.filter(Boolean);
        if (restored.length > 0) setSelectedSuffix(restored);
      }
      const restoredDateRange = parseStoredDateRange(parsed.dateRange);
      if (restoredDateRange) setDateRange(restoredDateRange);
    } catch (e) {
      // Ignore bad JSON / storage issues
    } finally {
      setFiltersHydrated(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist filters whenever they change.
  useEffect(() => {
    if (!filtersHydrated) return;
    try {
      const payload = {
        financialYear,
        isSummary,
        selectedPropertyGroups: Array.isArray(selectedPropertyGroups)
          ? selectedPropertyGroups
          : [],
        selectedCodeNo: Array.isArray(selectedCodeNo) ? selectedCodeNo : [],
        selectedPartyName: selectedPartyName || "",
        selectedMediaPlanners: selectedMediaPlanners || "",
        selectedSuffix: Array.isArray(selectedSuffix) ? selectedSuffix : [],
        dateRange: serializeDateRange(dateRange),
      };
      window.sessionStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify(payload),
      );
    } catch (e) {
      // Ignore quota / storage issues
    }
  }, [
    filtersHydrated,
    financialYear,
    isSummary,
    selectedPropertyGroups,
    selectedCodeNo,
    selectedPartyName,
    selectedMediaPlanners,
    selectedSuffix,
    dateRange,
  ]);

  const reportQueryParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("financialYear", financialYear);
    params.set("isSummary", isSummary ? "true" : "false");
    if (Array.isArray(selectedPropertyGroups) && selectedPropertyGroups.length > 0)
      params.set("propertyGroups", selectedPropertyGroups.join(","));
    if (Array.isArray(selectedCodeNo) && selectedCodeNo.length > 0)
      params.set("uniqueCodeNo", selectedCodeNo.join(","));
    if (selectedPartyName) params.set("uniquePartyName", selectedPartyName);
    if (selectedMediaPlanners) params.set("mediaPlanners", selectedMediaPlanners);
    if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0)
      params.set("suffix", selectedSuffix.join(","));
    const startDate = formatReportDate(dateRange?.[0]);
    const endDate = formatReportDate(dateRange?.[1]);
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    return params.toString();
  }, [
    financialYear,
    isSummary,
    selectedPropertyGroups,
    selectedCodeNo,
    selectedPartyName,
    selectedMediaPlanners,
    selectedSuffix,
    dateRange,
  ]);

  const beginRequest = useLatestRequest();

  useEffect(() => {
    if (!filtersHydrated) return;

    const isCurrent = beginRequest();
    const fetchData = async () => {
      setLoading(true);
      setSummaryTotal(null);
      setGrandTotals(null);
      try {
        const url = `${apiBaseUrl}/inventoryManagement/getPropertyAndMediaWiseSalesReport${reportQueryParams ? `?${reportQueryParams}` : ""}`;
        const res = await fetch(url, { method: "GET", credentials: "include" });
        const j = await res.json();
        if (!isCurrent()) return;
        if (j.success) {
          setData(
            (j.data || []).map((row) => ({
              ...row,
              soId: resolveSalesOrderId(row),
            })),
          );
          setGrandTotals(j.grandTotals ?? null);
          if (isSummary && j.total != null) setSummaryTotal(j.total);
          if (!isSummary) {
            if (j.propertyGroups) setPropertyGroups(j.propertyGroups || []);
            if (j.uniqueCodeNo) setUniqueCodeNo(j.uniqueCodeNo || []);
            if (j.uniquePartyName) setUniquePartyName(j.uniquePartyName || []);
            if (j.mediaPlanners) setMediaPlanners(j.mediaPlanners || []);
          }
        } else {
          message.error(j.message || "Failed to fetch report");
          setGrandTotals(null);
        }
      } catch (err) {
        if (!isCurrent()) return;
        console.error("Error fetching Property & Media-Wise Sales report:", err);
        message.error("Error fetching report");
        setGrandTotals(null);
      } finally {
        if (isCurrent()) setLoading(false);
      }
    };
    fetchData();
  }, [apiBaseUrl, reportQueryParams, isSummary, filtersHydrated]);

  const handleDownloadExcel = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      params.set("financialYear", financialYear);
      params.set("isExcel", "true");
      params.set("isSummary", isSummary ? "true" : "false");
      if (Array.isArray(selectedPropertyGroups) && selectedPropertyGroups.length > 0)
        params.set("propertyGroups", selectedPropertyGroups.join(","));
      if (Array.isArray(selectedCodeNo) && selectedCodeNo.length > 0)
        params.set("uniqueCodeNo", selectedCodeNo.join(","));
      if (selectedPartyName) params.set("uniquePartyName", selectedPartyName);
      if (selectedMediaPlanners) params.set("mediaPlanners", selectedMediaPlanners);
      if (Array.isArray(selectedSuffix) && selectedSuffix.length > 0)
        params.set("suffix", selectedSuffix.join(","));
      const startDate = formatReportDate(dateRange?.[0]);
      const endDate = formatReportDate(dateRange?.[1]);
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      const url = `${apiBaseUrl}/inventoryManagement/getPropertyAndMediaWiseSalesReport?${params.toString()}`;
      const res = await fetch(url, { method: "GET", credentials: "include" });
      if (!res.ok) {
        message.error("Failed to download Excel");
        return;
      }
      const blob = await res.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = isSummary
        ? `Property_Media_Wise_Sales_Summary_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`
        : `Property_Media_Wise_Sales_${financialYear}_${moment().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error("Error downloading Excel:", err);
      message.error("Error downloading Excel");
    } finally {
      setDownloading(false);
    }
  };

  const propertyGroupOptions = (propertyGroups || []).map((g) => ({ label: g, value: g }));
  const codeNoOptions = (uniqueCodeNo || []).map((c) => ({ label: c, value: c }));
  const partyNameOptions = (uniquePartyName || []).map((p) => ({ label: p, value: p }));
  const mediaPlannerOptions = (mediaPlanners || []).map((m) => ({ label: m?.name ?? m, value: m?.name ?? m }));

  const list = data || [];

  const renderSalesNo = useCallback(
    (row) => {
      const v = row.salesNo;
      const soId = row?.soId;
      if (!v || !soId) {
        return v ?? "-";
      }

      return (
        <span
          role="link"
          tabIndex={0}
          style={{
            color: "#1677ff",
            cursor: "pointer",
            textDecoration: "underline",
          }}
          onClick={() => navigate(`/sales-order/${soId}?tab=1`)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              navigate(`/sales-order/${soId}?tab=1`);
            }
          }}
          title="Open Sales Order"
        >
          {v}
        </span>
      );
    },
    [navigate],
  );

  return (
    <div
      className="parent-padding-container"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        padding: "20px",
        minHeight: "100vh",
      }}
    >
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Space>
              <Button icon={<FaArrowLeft />} onClick={onBack} type="text" />
              Property & Media-Wise Sales
            </Space>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "12px",
                padding: "12px 0",
                marginBottom: "4px",
              }}
            >
              <Space wrap size="middle">
                <Switch
                  checkedChildren="Summary"
                  unCheckedChildren="Detailed"
                  checked={isSummary}
                  onChange={setIsSummary}
                />
                <Select
                  value={financialYear}
                  onChange={setFinancialYear}
                  style={{ width: 120 }}
                  placeholder="Select FY"
                >
                  {getFinancialYearOptions().map((fy) => (
                    <Option key={fy} value={fy}>
                      {fy}
                    </Option>
                  ))}
                </Select>
                <Select
                  mode="multiple"
                  placeholder="Filter by Property Group"
                  options={propertyGroupOptions}
                  value={selectedPropertyGroups.length ? selectedPropertyGroups : undefined}
                  onChange={(v) => setSelectedPropertyGroups(v ?? [])}
                  style={{ minWidth: 260 }}
                  allowClear
                  showSearch
                filterOption={(input, opt) => {
                  const label = String(opt?.label ?? "").toLowerCase();
                  const query = String(input ?? "").toLowerCase();
                  if (!query) return true;
                  return label.startsWith(query);
                }}
                />
                <Select
                  mode="multiple"
                  placeholder="Filter by Code No"
                  options={codeNoOptions}
                  value={selectedCodeNo.length ? selectedCodeNo : undefined}
                  onChange={(v) => setSelectedCodeNo(v ?? [])}
                  style={{ minWidth: 160 }}
                  allowClear
                  showSearch
                  filterOption={(input, opt) => {
                    const label = String(opt?.label ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                />
                <Select
                  placeholder="Filter by Party Name"
                  options={partyNameOptions}
                  value={selectedPartyName}
                  onChange={setSelectedPartyName}
                  style={{ minWidth: 280 }}
                  allowClear
                  showSearch
                  filterOption={(input, opt) => {
                    const label = String(opt?.label ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                />
              </Space>
              <Space wrap size="middle">
                <Select
                  mode="multiple"
                  placeholder="Filter by Suffix"
                  options={DEFAULT_SUFFIX_OPTIONS}
                  value={selectedSuffix.length ? selectedSuffix : undefined}
                  onChange={(v) => setSelectedSuffix(v ?? [])}
                  style={{ minWidth: 160 }}
                  allowClear
                  dropdownRender={selectAllDropdownRender(
                    DEFAULT_SUFFIX_OPTIONS,
                    selectedSuffix,
                    setSelectedSuffix,
                  )}
                />
                <Select
                  placeholder="Filter by Media Planner"
                  options={mediaPlannerOptions}
                  value={selectedMediaPlanners}
                  onChange={setSelectedMediaPlanners}
                  style={{ minWidth: 240 }}
                  allowClear
                  showSearch
                  filterOption={(input, opt) => {
                    const label = String(opt?.label ?? "").toLowerCase();
                    const query = String(input ?? "").toLowerCase();
                    if (!query) return true;
                    return label.startsWith(query);
                  }}
                />
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format={REPORT_DATE_FORMAT}
                  style={{ width: 280 }}
                  allowClear
                />
                <Button
                  type="primary"
                  icon={<FaDownload />}
                  loading={downloading}
                  onClick={handleDownloadExcel}
                >
                  Download Excel
                </Button>
              </Space>
            </div>
          </div>
        }
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 60 }}>
            <Spin />
          </div>
        ) : list.length === 0 && !grandTotals ? (
          <div style={{ textAlign: "center", padding: 60, color: "#999" }}>No records found</div>
        ) : isSummary ? (
          <Table
            columns={[
              { title: "Property Code", dataIndex: "propertyCode", key: "propertyCode", width: 180, align: "center" },
              { title: "Amount", dataIndex: "amount", key: "amount", width: 140, align: "center", render: formatCurrency },
            ]}
            dataSource={list}
            rowKey={(r, i) => r.propertyCode ?? `summary-${i}`}
            pagination={false}
            size="small"
            components={{
              header: {
                cell: (props) => <th {...props} style={{ ...props.style, backgroundColor: "#daeffe" }} />,
              },
            }}
            summary={() => {
              const amt =
                grandTotals?.amount != null && grandTotals.amount !== ""
                  ? grandTotals.amount
                  : summaryTotal;
              return amt != null && amt !== "" ? (
                <Table.Summary>
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} align="center">
                      <strong>Grand Total</strong>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align="center">
                      <strong>{formatGrandTotalAmount(amt)}</strong>
                    </Table.Summary.Cell>
                  </Table.Summary.Row>
                </Table.Summary>
              ) : null;
            }}
          />
        ) : (
          <div style={{ overflow: "auto", maxHeight: 600 }}>
            <table
              style={{
                width: "100%",
                minWidth: 2840,
                borderCollapse: "collapse",
                fontSize: 12,
              }}
            >
              <thead>
                <tr>
                  <th style={{ ...thStyle, width: 110, textAlign: "center" }}>Sales No</th>
                  <th style={{ ...thStyle, width: 100, textAlign: "center" }}>Date</th>
                  <th style={{ ...thStyle, width: 180, textAlign: "left" }}>Party Name</th>
                  <th style={{ ...thStyle, width: 100, textAlign: "left" }}>Party City</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "left" }}>Property Group</th>
                  <th style={{ ...thStyle, width: 160, textAlign: "center" }}>Hoarding/Trading/Production</th>
                  <th style={{ ...thStyle, width: 100, textAlign: "center" }}>Code No</th>
                  <th style={{ ...thStyle, width: 220, textAlign: "left" }}>Location</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "left" }}>Area</th>
                  <th style={{ ...thStyle, width: 100, textAlign: "left" }}>Zone</th>
                  <th style={{ ...thStyle, width: 100, textAlign: "center" }}>Booking Days</th>
                  <th style={{ ...thStyle, width: 100, textAlign: "center" }}>From Date</th>
                  <th style={{ ...thStyle, width: 100, textAlign: "center" }}>To Date</th>
                  <th style={{ ...thStyle, width: 110, textAlign: "right" }}>Amount</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "center" }}>Bill No</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "left" }}>Salesman</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "left" }}>Media Planner</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "left" }}>Brand Segment</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "left" }}>Brand Name</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "left" }}>Mother Brand</th>
                  <th style={{ ...thStyle, width: 70, textAlign: "center" }}>Suffix</th>
                  <th style={{ ...thStyle, width: 120, textAlign: "right" }}>Purchase Amount</th>
                  <th style={{ ...thStyle, width: 90, textAlign: "center" }}>Category</th>
                  <th style={{ ...thStyle, width: 70, textAlign: "center" }}>Width</th>
                  <th style={{ ...thStyle, width: 70, textAlign: "center" }}>Height</th>
                </tr>
              </thead>
              <tbody>
                {list.map((row, idx) => (
                  <tr key={(row.salesNo || "") + (row.codeNo || "") + (row.fromDate || "") + idx}>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{renderSalesNo(row)}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.date ? moment(row.date).format("DD-MM-YYYY") : "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.partyName ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.partyCity ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.propertyGroup ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.hoardingTradingProduction ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.codeNo ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.location ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.area ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.zone ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.bookingDays ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.fromDate ? moment(row.fromDate).format("DD-MM-YYYY") : "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.toDate ? moment(row.toDate).format("DD-MM-YYYY") : "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{formatCurrency(row.amount)}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.billNo ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.salesman ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.mediaPlanner ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.brandSegment ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.brandName ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "left" }}>{row.motherBrand ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.suffix ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "right" }}>{formatCurrency(row.purchaseAmount)}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.category ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.width ?? "-"}</td>
                    <td style={{ ...tdStyle, textAlign: "center" }}>{row.height ?? "-"}</td>
                  </tr>
                ))}
              </tbody>
              {grandTotals && (
                <tfoot>
                  <tr>
                    <td
                      colSpan={13}
                      style={{
                        ...tdStyle,
                        textAlign: "center",
                        fontWeight: "bold",
                        backgroundColor: "#f0f9ff",
                      }}
                    >
                      Grand Total
                    </td>
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: "right",
                        fontWeight: "bold",
                        backgroundColor: "#f0f9ff",
                      }}
                    >
                      {formatGrandTotalAmount(grandTotals.amount)}
                    </td>
                    <td colSpan={7} style={{ ...tdStyle, backgroundColor: "#f0f9ff" }} />
                    <td
                      style={{
                        ...tdStyle,
                        textAlign: "right",
                        fontWeight: "bold",
                        backgroundColor: "#f0f9ff",
                      }}
                    >
                      {formatGrandTotalAmount(grandTotals.purchaseAmount)}
                    </td>
                    <td colSpan={3} style={{ ...tdStyle, backgroundColor: "#f0f9ff" }} />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default PropertyAndMediaWiseSalesReport;
